import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { useGetBenefitVerifiedStatusQuery } from "store/queries/benefitComparison";
import {
  IComparisonFilters,
  IOpenCluster,
  IRegressedCheck,
  IVerificationCell,
} from "types/BenefitComparison/BenefitComparison";

import {
  COMPARISON_BENEFIT_LABELS,
  formatComparisonValue,
  TRUTH_SOURCE_LABELS,
  VERIFIED_NO_WINDOW_NOTE,
  VERIFIED_REPORTS_ONLY_NOTE,
  verifiedCellText,
  verifiedHeadlineText,
  verifiedRangeText,
} from "./benefitComparisonWording";
import { READ_FROM_LABELS, relativeTime } from "./remitAccuracyWording";

/**
 * CLE-2821 — VERIFIED CHECKS. Every check a human verdict or a remit has given a correct value to,
 * scored against what today's engine says, recomputed by the nightly run.
 *
 * 🚨 REGRESSED COMES FIRST AND ON ITS OWN. It means a change broke a value that used to be right,
 * which is a different thing from the backlog of things that were never right — putting the two in
 * one list would hide the only urgent row on the page.
 *
 * 🚨 EVERY FIGURE CARRIES ITS SAMPLE SIZE, and nothing on this screen changes anything: no
 * configuration, no verdict, no status.
 */

const nameBody = (row: {
  accountName: string;
  payerName: string;
  planName: string | null;
}) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <span className="text-color-secondary">
      {row.planName ?? "No plan name"}
    </span>
  </div>
);

/** The engine's answer, or why it gave none. A declined benefit is never counted as a match. */
const engineValueText = (
  value: number | null,
  declined: string | null,
  benefit: IVerificationCell["benefit"]
) => {
  if (declined)
    return declined === "NO_MAP_ROW" ? "not configured" : "declined";
  return value === null ? "no value" : formatComparisonValue(value, benefit);
};

/** The verified value and where it came from. 🚨 A remit outranks a verdict; a conflict is said, never resolved. */
const truthBody = (row: IRegressedCheck) => (
  <div className="flex flex-column gap-1">
    <span>{formatComparisonValue(row.truthValue, row.benefit)}</span>
    <span className="text-color-secondary">
      {TRUTH_SOURCE_LABELS[row.truthSource]}
      {row.conflict ? " · they disagree" : ""}
    </span>
  </div>
);

/** When it broke, and under which engine configuration — the traceable half of a regression. */
const brokeBody = (row: IRegressedCheck) => (
  <div className="flex flex-column gap-1">
    <span>{row.regressedAt ? relativeTime(row.regressedAt) : "—"}</span>
    <span className="text-color-secondary">
      {row.regressedConfigVersion ?? "no config version"}
    </span>
  </div>
);

/** 🚨 Both ids, always: the RequestID is the only one pVerify itself can look up. */
const idsBody = (responseId: string, requestId: string | null) => (
  <div className="flex flex-column gap-1">
    <span className="text-color-secondary">{responseId}</span>
    <span className="text-color-secondary">RequestID {requestId ?? "—"}</span>
  </div>
);

const regressedCheckIdsBody = (row: IRegressedCheck) =>
  idsBody(row.pverifyResponseId, row.pverifyRequestId);

const clusterIdsBody = (row: IOpenCluster) =>
  idsBody(row.examplePverifyResponseId, row.examplePverifyRequestId);

const regressedCountBody = (row: IVerificationCell) =>
  row.regressed > 0 ? (
    <span className="text-red-600 font-semibold">{row.regressed}</span>
  ) : (
    <span>0</span>
  );

/** A cluster's unresolved count, with its regressions called out rather than folded in. */
const clusterOpenBody = (row: IOpenCluster) =>
  row.regressed > 0 ? (
    <span className="text-red-600 font-semibold">
      {row.open + row.regressed} · {row.regressed} regressed
    </span>
  ) : (
    <span>{row.open}</span>
  );

function Totals({
  label,
  count,
  testId,
  severe,
}: {
  label: string;
  count: number;
  testId: string;
  severe?: boolean;
}) {
  return (
    <span
      data-testid={testId}
      className={severe && count > 0 ? "text-red-600 font-semibold" : ""}
    >
      <strong>{count.toLocaleString()}</strong> {label}
    </span>
  );
}

export function BenefitVerifiedStatus({
  filters,
}: {
  filters: IComparisonFilters;
}) {
  // 🚨 No date window is sent: the truth set is all-time, deliberately (see the API read's header).
  const { data, isLoading, isError } = useGetBenefitVerifiedStatusQuery({
    accountId: filters.accountId,
    payerId: filters.payerId,
    benefit: filters.benefit,
  });
  const verified = data?.data;

  return (
    <div data-testid="benefitVerified">
      <div className="flex flex-column gap-2 mb-3">
        <h3 className="m-0">Verified checks</h3>
        <span className="text-color-secondary">
          Checks where a human verdict or a remit established the correct value,
          scored against what today&apos;s configuration makes of the same
          stored response. {VERIFIED_NO_WINDOW_NOTE}{" "}
          {VERIFIED_REPORTS_ONLY_NOTE}
        </span>
      </div>

      {isError && (
        <Message severity="error" text="Verified checks could not be loaded." />
      )}

      {verified && (
        <>
          <div className="flex flex-wrap align-items-baseline gap-4 mb-3">
            <span className="text-xl" data-testid="benefitVerified.headline">
              {verifiedHeadlineText(verified.totals)}
            </span>
            <span className="text-color-secondary">
              {verified.lastComputedAt
                ? `derived ${relativeTime(verified.lastComputedAt)}`
                : "the nightly run has not derived these yet"}
            </span>
          </div>

          <div
            className="flex flex-wrap gap-4 mb-4 text-color-secondary"
            data-testid="benefitVerified.totals"
          >
            <Totals
              label="open"
              count={verified.totals.open}
              testId="benefitVerified.open"
            />
            <Totals
              label="fixed"
              count={verified.totals.fixed}
              testId="benefitVerified.fixed"
            />
            <Totals
              label="regressed"
              count={verified.totals.regressed}
              testId="benefitVerified.regressed"
              severe
            />
            <Totals
              label="always matching"
              count={verified.totals.matching}
              testId="benefitVerified.matching"
            />
            {verified.totals.conflicts > 0 && (
              <span data-testid="benefitVerified.conflicts">
                ⚠️ {verified.totals.conflicts.toLocaleString()} where a remit
                and a verdict disagree — the remit is what is scored, and both
                are kept
              </span>
            )}
          </div>

          {verified.totals.regressed > 0 && (
            <Message
              severity="error"
              className="mb-3 w-full justify-content-start"
              data-testid="benefitVerified.regressedBanner"
              text={`${verified.totals.regressed.toLocaleString()} verified ${
                verified.totals.regressed === 1 ? "check" : "checks"
              } used to match and no longer do. A later change broke them.`}
            />
          )}

          {verified.regressed.length > 0 && (
            <DataTable
              value={verified.regressed}
              className="mb-5"
              data-testid="benefitVerified.regressedRows"
            >
              <Column header="Account · Payer · Plan" body={nameBody} />
              <Column
                header="Benefit"
                body={(row: IRegressedCheck) =>
                  COMPARISON_BENEFIT_LABELS[row.benefit]
                }
              />
              <Column header="Verified value" body={truthBody} />
              <Column
                header="Engine today"
                body={(row: IRegressedCheck) =>
                  engineValueText(
                    row.engineValue,
                    row.engineDeclined,
                    row.benefit
                  )
                }
              />
              <Column header="Broke" body={brokeBody} />
              {/* 🚨 Both ids, always: the RequestID is the only one pVerify itself can look up. */}
              <Column header="Check" body={regressedCheckIdsBody} />
            </DataTable>
          )}
        </>
      )}

      <div className="flex flex-column gap-2 mt-4 mb-3">
        <h3 className="m-0">By benefit</h3>
        <span className="text-color-secondary">
          How many verified checks today&apos;s engine matches, per benefit.
          Every figure carries its sample size.
        </span>
      </div>
      <DataTable
        value={verified?.benefits ?? []}
        loading={isLoading}
        emptyMessage="No verified checks yet."
        data-testid="benefitVerified.benefits"
      >
        <Column
          header="Benefit"
          body={(row: IVerificationCell) =>
            COMPARISON_BENEFIT_LABELS[row.benefit]
          }
        />
        <Column
          header="Engine matches"
          body={(row: IVerificationCell) => verifiedCellText(row)}
        />
        <Column header="Open" body={(row: IVerificationCell) => row.open} />
        <Column header="Fixed" body={(row: IVerificationCell) => row.fixed} />
        <Column header="Regressed" body={regressedCountBody} />
        <Column
          header="Remit / verdict conflicts"
          body={(row: IVerificationCell) => row.conflicts}
        />
      </DataTable>

      <div className="flex flex-column gap-2 mt-5 mb-3">
        <h3 className="m-0">Still open</h3>
        <span className="text-color-secondary">
          Verified checks today&apos;s engine gets wrong, grouped by account,
          payer, plan, benefit and where the engine read — the same clusters as
          the disagreement list, so one fix closes the checks beside it.
        </span>
      </div>
      <DataTable
        value={verified?.openClusters ?? []}
        loading={isLoading}
        emptyMessage="Nothing open: today's engine matches every verified check in this filter."
        data-testid="benefitVerified.openClusters"
      >
        <Column header="Account · Payer · Plan" body={nameBody} />
        <Column
          header="Benefit"
          body={(row: IOpenCluster) => COMPARISON_BENEFIT_LABELS[row.benefit]}
        />
        <Column header="Open" body={clusterOpenBody} />
        <Column header="Matching" body={(row: IOpenCluster) => row.matches} />
        <Column
          header="Verified value"
          body={(row: IOpenCluster) =>
            verifiedRangeText(row.truthMin, row.truthMax, row.benefit)
          }
        />
        <Column
          header="Engine today"
          body={(row: IOpenCluster) =>
            row.engineDeclined > 0 && row.engineMin === null
              ? "not configured"
              : verifiedRangeText(row.engineMin, row.engineMax, row.benefit)
          }
        />
        <Column
          header="Engine read from"
          body={(row: IOpenCluster) =>
            row.readFrom ? READ_FROM_LABELS[row.readFrom] ?? row.readFrom : "—"
          }
        />
        <Column header="Example check" body={clusterIdsBody} />
      </DataTable>
    </div>
  );
}
